// =============================================================================
// mediaQueries.ts - Breakpoints and pre-computed media query strings
// =============================================================================
// Exports two objects:
//   breakpoints: numeric pixel values for MUI breakpoints system
//   mediaQueries: pre-computed @media strings for use in Emotion/styled templates
//
// Also exports mediaQueriesValues as an alias of breakpoints for backward compat.
// =============================================================================

// ---------------------------------------------------------------------------
// Breakpoint values - raw pixel numbers from Figma breakpoint modes
// These map to MUI's breakpoint system (xs, sm, md, lg, xl).
// ---------------------------------------------------------------------------
export const breakpoints = {
  xs: 0,
  sm: 375,
  md: 768,
  lg: 1280,
  xl: 1440,
  xxl: 1920,
} as const;

// ---------------------------------------------------------------------------
// Backward-compatible alias: existing code imports { mediaQueriesValues }
// ---------------------------------------------------------------------------
export const mediaQueriesValues = {
  xs: breakpoints.xs,
  sm: breakpoints.md,
  md: breakpoints.lg,
  lg: breakpoints.lg,
  xl: breakpoints.xxl,
} as const;

// ---------------------------------------------------------------------------
// Media query strings - pre-computed for direct use in Emotion templates
// Mobile-first: min-width queries (styles apply at breakpoint and above)
// ---------------------------------------------------------------------------
export const mediaQueries = {
  mobile: `@media (max-width: ${breakpoints.md - 1}px)`,
  mobileOnly: `@media (max-width: ${breakpoints.md - 1}px)`,
  tablet: `@media (min-width: ${breakpoints.md}px)`,
  tabletOnly: `@media (min-width: ${breakpoints.md}px) and (max-width: ${breakpoints.lg - 1}px)`,
  desktop: `@media (min-width: ${breakpoints.lg}px)`,
  desktopM: `@media (min-width: ${breakpoints.lg}px)`,
  desktopL: `@media (min-width: ${breakpoints.xl}px)`,
  desktopXl: `@media (min-width: ${breakpoints.xxl}px)`,
} as const;

// ---------------------------------------------------------------------------
// Layout constants - content area horizontal padding per breakpoint
// Derived from Figma breakpoint modes (horizontalPadding values).
// ---------------------------------------------------------------------------
export const layoutPadding = {
  mobile: 16,
  desktop: 80,
  desktopL: 120,
  desktopXl: 360,
} as const;

export type Breakpoints = typeof breakpoints;
export type MediaQueries = typeof mediaQueries;
